import { TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description Base InputText implementation
 * @param BasicInput Base widget for extending
 * @returns Input Text class
 */
function InputTextClassCreator(BasicInput: import('widgets/forms/BasicInput').TBasicInput) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputText
     * @augments BasicInput
     * @classdesc Input type `text` implementation. Represents input `text` element together with widget-related HTML markup.
     * HTML structure assembled on backend and injected into resulted html markup by using `formElement` component
     * and dynamic forms configuration JSON.
     * @property {string} data-widget - Widget name `inputText`
     * @example <caption>InputText definition in dynamicForms.js</caption>
     * ...
     * // fields -> input -> text
     * text: {
     *     extends: 'fields.input.base',
     *     validation: {
     *         'patterns.security': 'validation.patterns.security',
     *         'errors.security': 'validation.errors.parse'
     *     },
     *     element: {
     *         minLength: 2,
     *         maxLength: 256,
     *         type: 'text'
     *     }
     * },
     * ...
     * // fields -> generic -> cardType
     * cardType: {
     *     extends: 'fields.input.text',
     *     'label.text': 'form.payment.card.type',
     *     element: {
     *         type: 'hidden',
     *         maxLength: 20,
     *         required: true
     *     }
     * },
     * ...
     * @example <caption>Insertion of InputText inside ISML templates</caption>
     * <isset name="formElement" value="${require('forms/formElement')}" scope="page"/>
     * ...
     * <form>
     *     ...
     *     <isprint value="${
     *         formElement(pdict.addressForm.address1, pdict.addressFormOptions).render()
     *     }" encoding="off"/>
     *     ...
     * </form>
     * @example <caption>Resulted HTML structure for InputText</caption>
     * <div data-widget="inputText" class="b-form_field m-required m-invalid"
     *     data-id="dwfrm_shipping_shippingAddress_addressFields_lastName" data-validation-config="... validation config">
     *     <label class="b-form_field-label" for="dwfrm_shipping_shippingAddress_addressFields_lastName">
     *         <span class="b-form_field-required" aria-hidden="true">*</span>
     *         Last Name
     *     </label>
     *     <input data-ref="field" id="dwfrm_shipping_shippingAddress_addressFields_lastName" type="text"
     *         class="b-input m-invalid" placeholder="" aria-describedby="dwfrm_shipping_shippingAddress_addressFields_lastName-error"
     *         pattern="..." name="dwfrm_shipping_shippingAddress_addressFields_lastName" required="" value="" maxlength="50"
     *         aria-required="true" minlength="2" autocomplete="family-name" data-event-blur="validate"
     *     >
     *     <div role="alert" class="b-form_field-message" data-ref="errorFeedback" id="dwfrm_shipping_shippingAddress_addressFields_lastName-error">This field is required.</div>
     * </div>
     */
    class InputText extends BasicInput {
        /**
         * @description Element on input handler
         * @listens dom#input
         * @emits InputText#input
         * @param el source of event
         * @param event event instance if DOM event
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onInput(el: TRefElementInstance, event?: Event) {
            /**
             * @description Event, indicates that `input` event happened
             * @event InputText#input
             */
            this.emit('input');
        }
    }

    return InputText;
}

export type TInputText = ReturnType<typeof InputTextClassCreator>;

export type TInputTextInstance = InstanceType<TInputText>;

export default InputTextClassCreator;
